/**
 * /my-trouve-chats — List of past Trouvé chat summaries.
 *
 * Each row shows the conversation title, the auto-generated summary
 * (Trouvé's "note" — the one-paragraph recap from the finalize call),
 * and a delete action. Rows are sorted newest first.
 *
 * No filters / search yet — we'll add those once volume justifies.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Trash2, MessageCircle, Loader2, ArrowLeft, Download } from "lucide-react";
import Header from "../components/Header";
import {
  listTrouveSessions,
  deleteTrouveSession,
  downloadTrouveAudio,
} from "../lib/api";

const formatWhen = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "";
  }
};

const MyTrouveChats = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listTrouveSessions(100);
      setSessions(rows);
    } catch {
      toast.error(t("trouveChat.errorGeneric"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("trouveChat.deleteChatConfirm"))) return;
    try {
      await deleteTrouveSession(id);
      setSessions((prev) => prev.filter((s) => s.session_id !== id));
      toast.success(t("common.delete"));
    } catch {
      toast.error(t("trouveChat.errorGeneric"));
    }
  };

  const handleDownloadSummary = async (s) => {
    if (!s?.summary) return;
    try {
      const filename = await downloadTrouveAudio(s.summary, {
        hint: s.title || "summary",
      });
      toast.success(filename);
    } catch {
      toast.error(t("trouveChat.errorGeneric"));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#9B6A6C]"
            data-testid="trouve-saved-back"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common.back")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/trouve/chat")}
            className="inline-flex items-center gap-1.5 bg-[#9B6A6C] hover:bg-[#8a5a5d] text-white rounded-full px-4 py-2 text-sm font-medium"
            data-testid="trouve-saved-new-chat"
          >
            <MessageCircle className="h-4 w-4" />
            {t("trouveChat.newChat")}
          </button>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#FBE7EE] to-[#9B6A6C] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif italic text-[#2C2123]">
            {t("trouveChat.savedChatsTitle")}
          </h1>
        </div>

        {loading ? (
          <div
            className="flex items-center justify-center py-20"
            data-testid="trouve-saved-loading"
          >
            <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
          </div>
        ) : sessions.length === 0 ? (
          <div
            className="bg-white rounded-2xl border border-[#EADDD7] p-8 text-center text-[#5C4B51]"
            data-testid="trouve-saved-empty"
          >
            <p className="text-sm mb-5">{t("trouveChat.savedChatsEmpty")}</p>
            <button
              type="button"
              onClick={() => navigate("/trouve/chat")}
              className="inline-block bg-[#9B6A6C] hover:bg-[#8a5a5d] text-white rounded-full px-6 py-2.5 text-sm font-medium"
              data-testid="trouve-saved-empty-cta"
            >
              {t("trouveChat.newChat")}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="trouve-saved-list">
            {sessions.map((s) => (
              <article
                key={s.session_id}
                className="bg-white rounded-2xl border border-[#EADDD7] p-5 hover:border-[#9B6A6C]/40 transition-colors"
                data-testid={`trouve-saved-row-${s.session_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-medium text-[#2C2123] text-base leading-snug truncate">
                    {s.title || t("trouveChat.headerTitle")}
                  </h3>
                  <div className="flex items-center gap-1 shrink-0">
                    {s.summary && (
                      <button
                        type="button"
                        onClick={() => handleDownloadSummary(s)}
                        aria-label={t("trouveChat.saveAudio")}
                        title={t("trouveChat.saveAudio")}
                        className="h-8 w-8 rounded-full hover:bg-[#FBE7EE] flex items-center justify-center text-[#8A7D82] hover:text-[#9B6A6C] transition-colors"
                        data-testid={`trouve-saved-download-${s.session_id}`}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(s.session_id)}
                      aria-label={t("trouveChat.deleteChatCta")}
                      className="h-8 w-8 rounded-full hover:bg-[#FBE7EE] flex items-center justify-center text-[#8A7D82] hover:text-[#9B6A6C] transition-colors"
                      data-testid={`trouve-saved-delete-${s.session_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-[#8A7D82] uppercase tracking-wider mb-2">
                  {t("trouveChat.summaryEyebrow")} ·{" "}
                  {t("trouveChat.summaryFromTime", { when: formatWhen(s.updated_at) })}{" "}
                  · {t("trouveChat.messageCountLabel", { count: s.message_count })}
                </div>
                {s.summary ? (
                  <p className="text-sm text-[#5C4B51] leading-relaxed">{s.summary}</p>
                ) : (
                  <p className="text-sm text-[#8A7D82] italic">…</p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTrouveChats;
